/**
 * ScreenerPage
 *
 * HEY-style sender screening. Review first-time senders before they
 * reach the inbox. Tabs for pending, approved, and rejected senders.
 */

import { useState, useCallback } from 'react';
import { Shield, Check, X, UserCheck, UserX, PartyPopper, Undo2, Mail } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useEmailAccounts } from '../hooks/useEmailAccounts';
import { useScreener } from '../hooks/useScreener';
import { useEmailModules } from '../hooks/useEmailModules';
import type { EmailScreenedSender, EmailScreenedSenderWithMessage } from '@/lib/api';

function PendingSenderCard({
  sender,
  onApprove,
  onReject,
}: {
  sender: EmailScreenedSenderWithMessage;
  onApprove: () => void;
  onReject: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between gap-3">
          <div
            className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
              {(sender.senderName || sender.senderAddress).charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">
                {sender.senderName || sender.senderAddress}
              </div>
              <div className="text-xs text-muted-foreground truncate">{sender.senderAddress}</div>
              {sender.triggeringMessage && (
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {sender.triggeringMessage.subject}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="gap-1 text-green-600 border-green-200 hover:bg-green-50"
              onClick={onApprove}
            >
              <Check className="h-3.5 w-3.5" /> Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
              onClick={onReject}
            >
              <X className="h-3.5 w-3.5" /> Reject
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && sender.triggeringMessage && (
        <CardContent className="px-4 pb-4 pt-0 border-t border-border mt-2">
          <div className="bg-muted/50 rounded-md p-3 mt-2">
            <div className="text-xs text-muted-foreground mb-1">
              {new Date(sender.triggeringMessage.createdAt).toLocaleString()}
            </div>
            <div className="text-sm font-medium mb-1">{sender.triggeringMessage.subject}</div>
            <p className="text-sm text-muted-foreground">
              {sender.triggeringMessage.snippet || ''}
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function SenderRow({
  sender,
  actionLabel,
  actionIcon: ActionIcon,
  onAction,
}: {
  sender: EmailScreenedSender;
  actionLabel: string;
  actionIcon: React.ComponentType<{ className?: string }>;
  onAction: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium flex-shrink-0">
          {(sender.senderName || sender.senderAddress).charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm truncate">{sender.senderName || sender.senderAddress}</div>
          <div className="text-xs text-muted-foreground truncate">{sender.senderAddress}</div>
        </div>
      </div>
      <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={onAction}>
        <ActionIcon className="h-3.5 w-3.5" /> {actionLabel}
      </Button>
    </div>
  );
}

export function ScreenerPage() {
  const { activeAccount } = useEmailAccounts();
  const accountId = activeAccount?.id;
  const { isModuleEnabled } = useEmailModules(accountId);
  const { pending, approved, rejected, loading, approve, reject, updateStatus } =
    useScreener(accountId);
  const { toast } = useToast();

  const handleApprove = useCallback(
    async (senderId: string) => {
      const result = await approve(senderId);
      toast({
        title: 'Sender approved',
        description: result ? `${result.movedMessages} message(s) moved to inbox` : undefined,
      });
    },
    [approve, toast],
  );

  const handleReject = useCallback(
    async (senderId: string) => {
      const result = await reject(senderId);
      toast({
        title: 'Sender rejected',
        description: result ? `${result.deletedMessages} message(s) removed` : undefined,
      });
    },
    [reject, toast],
  );

  if (!isModuleEnabled('screener')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-6">
        <Shield className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm font-medium">Screener is not enabled</p>
        <p className="text-xs mt-1">Enable it in Settings &rarr; Modules</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5" /> Screener
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review first-time senders before they reach your inbox
          </p>
        </div>

        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending" className="gap-1.5">
              <Mail className="h-3.5 w-3.5" /> Pending
              {pending.length > 0 && <Badge className="text-xs px-1.5 h-5">{pending.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-1.5">
              <UserCheck className="h-3.5 w-3.5" /> Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-1.5">
              <UserX className="h-3.5 w-3.5" /> Rejected
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : pending.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <PartyPopper className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-sm font-medium">All caught up!</p>
                <p className="text-xs mt-1">No pending senders to review</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pending.map((sender) => (
                  <PendingSenderCard
                    key={sender.id}
                    sender={sender}
                    onApprove={() => handleApprove(sender.id)}
                    onReject={() => handleReject(sender.id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="approved" className="mt-4">
            {approved.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No approved senders yet
              </p>
            ) : (
              <div className="border border-border rounded-md">
                {approved.map((sender) => (
                  <SenderRow
                    key={sender.id}
                    sender={sender}
                    actionLabel="Revoke"
                    actionIcon={Undo2}
                    onAction={() => updateStatus(sender.id, 'pending')}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="rejected" className="mt-4">
            {rejected.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No rejected senders</p>
            ) : (
              <div className="border border-border rounded-md">
                {rejected.map((sender) => (
                  <SenderRow
                    key={sender.id}
                    sender={sender}
                    actionLabel="Approve"
                    actionIcon={Check}
                    onAction={() => updateStatus(sender.id, 'approved')}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
